/**
 * ReasoningStep Transformation Utilities
 *
 * Utility functions for transforming raw backend ReasoningStep data
 * into structured formats suitable for UI rendering.
 */
import { ReasoningStepType, ReasoningStepResultType } from "../types/chat";
const isRecord = (value) => typeof value === "object" && value !== null;
export const parseRequestFormResultPayload = (result) => {
    if (!isRecord(result))
        return undefined;
    const rejected = result.rejected === true;
    const submission = "submission" in result ? result.submission : undefined;
    return { rejected, submission };
};
/**
 * Type guard to check if result is already a structured ReasoningStepResult
 */
export function isStructuredResult(result) {
    return (result !== undefined &&
        typeof result === "object" &&
        "type" in result &&
        "data" in result &&
        Object.values(ReasoningStepResultType).includes(result.type));
}
/**
 * Transform raw backend result data into a structured ReasoningStepResult.
 *
 * This function handles the conversion of raw result data from the backend
 * into a format that the UI components can render properly.
 *
 * @param step - The reasoning step containing raw result data
 * @returns A structured ReasoningStepResult, or undefined if no result
 *
 * @example
 * ```ts
 * const structuredResult = transformReasoningStepResult(step)
 * // Returns: { type: 'links', data: [...], metadata: {...} }
 * ```
 */
export function transformReasoningStepResult(step) {
    if (!step.result)
        return undefined;
    // If already structured, return as-is
    if (isStructuredResult(step.result)) {
        return step.result;
    }
    const rawResult = step.result;
    // Default to JSON format
    const result = {
        type: ReasoningStepResultType.Json,
        data: rawResult,
    };
    switch (step.type) {
        case ReasoningStepType.ConsultantSearchWeb:
        case ReasoningStepType.ConsultantRetrieveContextObjs: {
            // Transform to links format if items array exists
            if (!Array.isArray(rawResult.items)) {
                break;
            }
            result.type = ReasoningStepResultType.Links;
            result.data = rawResult.items.map((item) => ({
                url: item.url || "",
                title: item.title,
                subtitle: item.subtitle,
                provider: item.provider,
                content: item.content,
            }));
            break;
        }
        case ReasoningStepType.ConsultantGenerateImage: {
            // Treat generated image references as links (title/url)
            if (!Array.isArray(rawResult.items)) {
                break;
            }
            result.type = ReasoningStepResultType.Links;
            result.data = rawResult.items.map((item) => ({
                url: item.url || "",
                title: item.title,
                subtitle: item.subtitle,
                provider: item.provider,
            }));
            break;
        }
        case ReasoningStepType.ConsultantLookup:
        case ReasoningStepType.ConsultantLookupAgent: {
            // Lookup tools typically return a list of items (similar to retrieval/search).
            if (!Array.isArray(rawResult.items)) {
                break;
            }
            result.type = ReasoningStepResultType.Links;
            result.data = rawResult.items.map((item) => ({
                url: item.url || "",
                title: item.title,
                subtitle: item.subtitle,
                provider: item.provider,
            }));
            break;
        }
        case ReasoningStepType.ConsultantFetchWebContent: {
            // Transform to list format with downloadable content
            if (!Array.isArray(rawResult.items)) {
                break;
            }
            result.type = ReasoningStepResultType.List;
            result.metadata = { downloadMarkdown: true };
            result.data = rawResult.items.map((item) => ({
                title: item.title,
                url: item.url,
                subtitle: item.provider,
                content: item.content || "",
            }));
            break;
        }
        case ReasoningStepType.ConsultantCallAgent: {
            // Transform to text format showing agent response
            if (!Array.isArray(rawResult.items) || rawResult.items.length === 0) {
                break;
            }
            const responseItem = rawResult.items[0];
            if (responseItem && responseItem.content) {
                result.type = ReasoningStepResultType.Text;
                result.data = responseItem.content;
                result.metadata = {
                    agentName: responseItem.title,
                    provider: responseItem.provider,
                };
            }
            break;
        }
        case ReasoningStepType.ConsultantTabularQuery: {
            result.type = ReasoningStepResultType.Tabular;
            result.data = rawResult;
            break;
        }
        case ReasoningStepType.ConsultantListKbFiles: {
            // Render the file listing as a List of "name (node_id: <uuid>)" entries.
            if (!Array.isArray(rawResult.items)) {
                break;
            }
            result.type = ReasoningStepResultType.List;
            result.data = rawResult.items.map((item) => {
                const name = item.title?.trim() || "";
                let nodeId;
                if (typeof item.content === "string" && item.content.length > 0) {
                    try {
                        const parsed = JSON.parse(item.content);
                        nodeId = parsed?.node_id;
                    }
                    catch {
                        // fall through; subtitle may carry the id
                    }
                }
                if (nodeId === undefined && item.subtitle) {
                    nodeId = item.subtitle;
                }
                return nodeId !== undefined ? `${name} (node_id: ${nodeId})` : name;
            });
            break;
        }
        case ReasoningStepType.ConsultantGetContextObjLines: {
            // Transform to text format showing document content with line metadata
            if (!Array.isArray(rawResult.items) || rawResult.items.length === 0) {
                break;
            }
            const responseItem = rawResult.items[0];
            if (!responseItem?.content)
                break;
            try {
                const payload = JSON.parse(responseItem.content);
                result.type = ReasoningStepResultType.Text;
                result.data = payload?.content ?? "";
                result.metadata = {
                    documentTitle: responseItem.title,
                    lineStart: payload?.line_start ??
                        step.args?.line_start ??
                        step.args?.lineStart,
                    lineEnd: payload?.line_end ??
                        step.args?.line_end ??
                        step.args?.lineEnd,
                    truncated: payload?.truncated,
                };
            }
            catch {
                result.type = ReasoningStepResultType.Text;
                result.data = responseItem.content;
                result.metadata = {
                    documentTitle: responseItem.title,
                    lineStart: step.args?.line_start ??
                        step.args?.lineStart,
                    lineEnd: step.args?.line_end ??
                        step.args?.lineEnd,
                };
            }
            break;
        }
        case ReasoningStepType.ConsultantRequestForm: {
            const payload = parseRequestFormResultPayload(step.result);
            if (!payload)
                break;
            const submission = payload.submission;
            const isRejected = payload.rejected === true;
            const hasSubmission = isRecord(submission) && !isRejected;
            const listItems = [];
            if (hasSubmission && Array.isArray(submission)) {
                for (const entry of submission) {
                    if (!entry || typeof entry !== "object")
                        continue;
                    const question = typeof entry.question === "string"
                        ? entry.question.trim()
                        : "";
                    if (!question)
                        continue;
                    const rawValue = entry.value;
                    let valueText = "";
                    if (rawValue === null || rawValue === undefined) {
                        valueText = "";
                    }
                    else if (Array.isArray(rawValue)) {
                        valueText = rawValue.map((item) => String(item)).join(", ");
                    }
                    else {
                        valueText = String(rawValue);
                    }
                    const normalizedValue = valueText.trim();
                    listItems.push(normalizedValue.length > 0
                        ? `${question}: ${normalizedValue}`
                        : question);
                }
            }
            result.type = ReasoningStepResultType.List;
            result.data = listItems;
            result.metadata = {
                requestFormNotFilled: listItems.length === 0 && !isRejected,
                requestFormRejected: isRejected,
            };
            break;
        }
        case ReasoningStepType.ConsultantFinished:
            // Finished step typically has no result to display
            return undefined;
    }
    return result;
}
/**
 * Normalize a ReasoningStep by transforming its result into structured format.
 *
 * Use this function when you need to ensure a step's result is in the
 * standardized ReasoningStepResult format for UI rendering.
 *
 * @param step - The reasoning step to normalize
 * @returns A new ReasoningStep with structured result
 *
 * @example
 * ```ts
 * const normalizedStep = normalizeReasoningStep(rawStep)
 * // result is now guaranteed to be ReasoningStepResult format
 * ```
 */
export function normalizeReasoningStep(step) {
    const transformedResult = transformReasoningStepResult(step);
    return {
        ...step,
        result: transformedResult ?? step.result,
    };
}
/**
 * Normalize an array of ReasoningSteps.
 *
 * @param steps - Array of reasoning steps to normalize
 * @returns Array of normalized reasoning steps
 */
export function normalizeReasoningSteps(steps) {
    if (!steps)
        return undefined;
    return steps.map(normalizeReasoningStep);
}
/**
 * Check if a reasoning step has completed (has an endTime).
 */
export function isStepCompleted(step) {
    return !!step.endTime;
}
/**
 * Check if all steps in an array are completed.
 */
export function areAllStepsCompleted(steps) {
    return steps.every(isStepCompleted);
}
/**
 * Check if the reasoning process has finished (has a ConsultantFinished step).
 */
export function hasFinishedStep(steps) {
    if (!steps || steps.length === 0)
        return false;
    return steps.some((step) => step.type === ReasoningStepType.ConsultantFinished);
}
/**
 * Calculate total duration of all steps in milliseconds.
 */
export function calculateTotalDuration(steps) {
    return steps.reduce((total, step) => {
        if (step.startTime && step.endTime) {
            const duration = new Date(step.endTime).getTime() - new Date(step.startTime).getTime();
            return total + duration;
        }
        return total + (step.duration || 0);
    }, 0);
}
